import React from "react";

/**
 * Which app to send the visitor to.
 *
 * The two funnels lead to different products: a passenger who scanned a
 * driver's QR wants SafeRides, while every other mobile visitor is a BeamLive
 * user. Sending either to the other's listing wastes the install.
 */
export type StoreVariant = "beamlive" | "saferides";

interface StoreLinks {
  ios: string | null;
  android: string | null;
}

const LINKS: Record<StoreVariant, StoreLinks> = {
  beamlive: {
    ios:
      process.env.REACT_APP_BEAMLIVE_IOS_STORE_URL ||
      "https://apps.apple.com/us/app/beamlive/id1570276190",
    android:
      process.env.REACT_APP_BEAMLIVE_ANDROID_STORE_URL ||
      "https://play.google.com/store/apps/details?id=beam.live.on",
  },
  saferides: {
    // Region-agnostic form of
    // https://apps.apple.com/us/app/beamsaferides/id6479255702 — Apple
    // redirects an id-only link to the visitor's own storefront, where the
    // /us/ path can show "not available in your region" to a passenger
    // scanning a QR outside the US.
    ios:
      process.env.REACT_APP_SAFERIDES_IOS_STORE_URL ||
      "https://apps.apple.com/app/id6479255702",
    // Play links ARE derivable from the package id, so this one is safe to
    // default — it matches applicationId in the SafeRides build.gradle.
    android:
      process.env.REACT_APP_SAFERIDES_ANDROID_STORE_URL ||
      "https://play.google.com/store/apps/details?id=beam.live.safeRides",
  },
};

const AppleIcon = () => (
  <svg viewBox="0 0 384 512" width="22" height="22" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const PlayIcon = () => (
  <svg viewBox="0 0 512 512" width="22" height="22">
    <path
      fill="#34A853"
      d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0z"
    />
    <path
      fill="#FBBC04"
      d="M48 511.9 304.9 256 48 .1C34.8 7 25.7 19.4 25.7 35.6v440.7c0 16.2 9.1 28.6 22.3 35.6z"
    />
    <path fill="#EA4335" d="M104.6 499 385.4 337.8l-60.1-60.1L104.6 499z" />
    <path
      fill="#4285F4"
      d="m385.4 174.2-60.1 60.1L385.4 295l92.3-52.9c14.5-8.4 14.5-29.6 0-38l-92.3-29.9z"
    />
  </svg>
);

/**
 * The App Store / Play Store pair, shared by the mobile interstitial and the
 * end of the referral funnel so the two never drift apart visually. Styling
 * comes from `.store-btn` in MobileLanding's stylesheet.
 *
 * Renders only the buttons whose URL is known — a store button that leads
 * nowhere is worse than no button at all.
 */
const StoreButtons: React.FC<{ variant: StoreVariant }> = ({ variant }) => {
  const links = LINKS[variant];
  const openExternal = (url: string) => {
    window.location.href = url;
  };

  if (!links.ios && !links.android) return null;

  return (
    <div className="mobile-landing__stores">
      {links.ios && (
        <button
          type="button"
          className="store-btn"
          onClick={() => openExternal(links.ios as string)}
        >
          <span className="store-btn__icon">
            <AppleIcon />
          </span>
          <span className="store-btn__text">
            <small>Download on the</small>
            <strong>App Store</strong>
          </span>
        </button>
      )}

      {links.android && (
        <button
          type="button"
          className="store-btn"
          onClick={() => openExternal(links.android as string)}
        >
          <span className="store-btn__icon">
            <PlayIcon />
          </span>
          <span className="store-btn__text">
            <small>GET IT ON</small>
            <strong>Google Play</strong>
          </span>
        </button>
      )}
    </div>
  );
};

export default StoreButtons;
